import type { Moment } from 'moment'
import type { AirportTransferJourneyType } from 'contexts/GlobalSearch/GlobalSearchState'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { onScrollingEnd } from 'lib/window/scrollUtils'
import { useCallback, useRef } from 'react'

export type AirportTransferSearchEmptyField = 'from' | 'to' | 'pickupDate' | 'returnDate'

interface AirportTransferSearchFieldState {
  searchItem?: App.SearchItem
  secondarySearchItem?: App.SearchItem
  checkinDate?: Moment
  checkoutDate?: Moment
  airportTransferJourneyType?: AirportTransferJourneyType
}

export interface CarsAirportTransferSearchFieldHandle {
  openFirstEmptyField: (field: AirportTransferSearchEmptyField | undefined) => void
}

export interface CarsAirportTransferSearchPromptHandle extends CarsAirportTransferSearchFieldHandle {
  scrollIntoView: () => void
}

export const CARS_AIRPORT_TRANSFER_SEARCH_ID = 'cars-airport-transfer-search'

function isAirportTransferSearchFocused() {
  const searchAnchor = document.getElementById(CARS_AIRPORT_TRANSFER_SEARCH_ID)
  const activeElement = document.activeElement
  return !!searchAnchor && !!activeElement && searchAnchor.contains(activeElement)
}

export function getFirstEmptyAirportTransferSearchField(
  state: AirportTransferSearchFieldState,
): AirportTransferSearchEmptyField | undefined {
  if (!state.searchItem) {
    return 'from'
  }

  if (!state.secondarySearchItem) {
    return 'to'
  }

  if (!state.checkinDate) {
    return 'pickupDate'
  }

  if (state.airportTransferJourneyType === 'return' && !state.checkoutDate) {
    return 'returnDate'
  }

  return undefined
}

function useAirportTransferSearchPrompt(searchState: AirportTransferSearchFieldState) {
  const isTabletUp = useScreenSizeUp('tablet')
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const searchPromptRef = useRef<CarsAirportTransferSearchPromptHandle>(null)
  const searchStateRef = useRef(searchState)
  searchStateRef.current = searchState

  const onAirportTransferSearchClick = useCallback(async() => {
    if (!isTabletUp) {
      toggleSearchVertical('cars')
      return
    }

    const searchPrompt = searchPromptRef.current
    searchPrompt?.scrollIntoView()
    await onScrollingEnd()

    if (isAirportTransferSearchFocused()) {
      return
    }

    searchPrompt?.openFirstEmptyField(getFirstEmptyAirportTransferSearchField(searchStateRef.current))
  }, [isTabletUp, toggleSearchVertical])

  return {
    searchPromptRef,
    onAirportTransferSearchClick,
  }
}

export default useAirportTransferSearchPrompt
